import { Component, OnInit, Inject, ViewChild, ElementRef, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatStepperModule, MatStepper } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatListModule } from '@angular/material/list';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MeterService } from '@services/meter.service';
import { UtilService } from '@services/util.service';
import { MeterDetails, Meter, MeterType, ConsentStatus, FuelType, MeterSelection, MeterPreview } from '@models/meter.model';
import { finalize } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { CustomRadioButtonComponent } from '@components/shared/custom-radio-button/custom-radio-button.component';
import { GenericTableComponent, TableColumn, TableConfig } from '@components/shared/generic-table/generic-table.component';
import { LoginService, UserDetails } from '@services/login.service';
import { isSmartMeter, isNonHHMeter, isSmartMeterConsentType, getMeterDetailsPreview, getConsentExpiryRange } from '@utils/meter.utils';
import { ConsentType, RelationshipType, ConsentRequest, Consent, NotificationStatus, ConsentCategory } from '@models/consent.model';
import { ConsentService } from '@services/consent.service';
import { NotificationService } from '@services/notification.service';
import { PreferencesService } from '@services/preferences.service';
import { LogService } from '@services/log.service';
import { AddHhConsentComponent } from '@components/shared/add-hh-consent.component';
import { CompanySelectionComponent } from '@components/shared/company-selection.component';
import { AddSmartConsentComponent } from '@components/shared/add-smart-consent/add-smart-consent.component';
import { AddNhhConsentComponent } from '@components/shared/add-nhh-consent.component';
import { SdkManagerService } from '@services/sdk-manager.service';
import { MeterSelectionComponent } from '../meter-selection/meter-selection.component';
import { VerificationAdapterService, VerificationDialogComponent, VerificationDialogData, VerificationDialogHelper } from '../verification-dialog';

export interface DialogData {
  isMultiMeter?: boolean; // Flag to determine single vs multi meter mode
  consentRequest?: ConsentRequest; // Include the full consent request data
  scope?: 'ANNUAL' | 'HH'; // Include the scope
}

@Component({
  selector: 'app-add-meter-dialog',
  standalone: true,
  imports: [
    CommonModule, 
    MatDialogModule,
    MatStepperModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatCheckboxModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatListModule,
    ReactiveFormsModule,
    CustomRadioButtonComponent,
    GenericTableComponent,
    MeterSelectionComponent,
    AddHhConsentComponent,
    AddSmartConsentComponent,
    AddNhhConsentComponent,
    CompanySelectionComponent
  ],
  templateUrl: './add-meter-dialog.component.html',
  styleUrls: ['./add-meter-dialog.component.scss']
})
export class 
AddMeterDialogComponent implements OnInit {
  loginService = inject(LoginService);
  notificationService = inject(NotificationService);  
  sdkManager = inject(SdkManagerService);
  scope: 'ANNUAL' | 'HH' | undefined;

  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild('addSmartConsent') addSmartConsentComponent!: AddSmartConsentComponent;
  loggedInUser: UserDetails | null = null;
  fuelTypeEnum = FuelType;
  meterTypeEnum = MeterType; // Add MeterType enum for template use
  meterForm: FormGroup;
  uploadingConsent = false;
  
  // Get expiry date range from utility function
  private expiryRange = getConsentExpiryRange();
  minExpiryDate = this.expiryRange.minExpiryDate;
  maxExpiryDate = this.expiryRange.maxExpiryDate;

  // Multi-meter support properties
  isMultiMeterMode = false;
  uploadedMeters: MeterPreview[] = [];
  isProcessingExcel = false;

  initialFormValue = signal<Partial<MeterSelection>>({});
  supportedMeterTypes: MeterType[] = [];
  supportedSmartMeterConsentTypes: ConsentType[] = [];
  
  // Consent request mode properties
  isConsentRequestMode = false;
  
  // Table configuration for multi-meter display
  multiMeterTableConfig: TableConfig = {
   enablePagination: true,
    enableSort: true,
    enableSearch: true,
    enableFilter: true,
    enableRowSelection: true,
    multipleSelection: true,
    pageSize: 10,
    pageSizeOptions: [5, 10, 25, 50]  
  };

  multiMeterColumns: TableColumn[] = [
    { key: 'address', header: 'Address', sortable: true },
    { key: 'meterNumber', header: 'Meter Number', sortable: true },
    { key: 'fuelType', header: 'Fuel Type', sortable: true }
  ];

  // New properties for relationship step
  relationshipOptions = this.loginService.getRelationshipOptions();
  isCompanyUser = this.loginService.isCompanyUser();

  @ViewChild('dialogContent') dialogContent!: ElementRef;
  
  constructor(
    private fb: FormBuilder,
    private meterService: MeterService,
    private consentService: ConsentService,
    private utilService: UtilService,
    private preferencesService: PreferencesService,
    private verificationAdapter: VerificationAdapterService,
    private dialogRef: MatDialogRef<AddMeterDialogComponent>,
    private dialog: MatDialog,
    private http: HttpClient,
    private logService: LogService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    // Set default expiry date to 1 year from now
    const defaultExpiryDate = new Date();
    defaultExpiryDate.setFullYear(defaultExpiryDate.getFullYear() + 3);
    this.loggedInUser = this.loginService.loginUser;
    
    this.meterForm = this.fb.group({
      // Step 1: Meter Information
      selectedMeter: [[], Validators.required], // Selected meters from meter-selection component
      
      // Step 2: Relationship with Property
      relationship: this.fb.group({
        type: ['', Validators.required]
      }),
      
      // Step 3: Documentation and Consent
      consentDetails: this.fb.group({
        consent: [null as Consent | null, Validators.required], // Now expects standardized Consent objects from both HH and Smart consent components
        consentTimestamp: [{ value: '', disabled: true }], // Readonly field to display when consent was added
        expiryDate: [defaultExpiryDate, Validators.required]
      })
    });

    // Get supported meter types from preferences
    const preferences = this.preferencesService.getPreferences();
    this.supportedMeterTypes = preferences.supportedMeterTypes;
    this.supportedSmartMeterConsentTypes = preferences.supportedSmartMeterConsentTypes;

    // Set multi-meter mode based on data
    this.isMultiMeterMode = this.data?.isMultiMeter || false;

    // Set consent request mode
    this.isConsentRequestMode = !!this.data?.consentRequest;

    if (this.data?.scope) {
      this.scope = this.data.scope;
    }

    // Prefill meter number if provided in consent request (single meter mode)
    if (this.data?.consentRequest) {
      if (!this.isMultiMeterMode) {
        // Single meter mode
        const firstMeterNumber = this.consentService.getFirstMeterFromRequest(this.data.consentRequest);
        if (firstMeterNumber) {
          this.initialFormValue.set({
            addressType: 'meterNo',
            meterNoSearch: {
              meterNo: firstMeterNumber,
              fuelType: this.data.consentRequest.meters[0]?.fuelType || FuelType.ELEC,
            }
          })
        }
      } else {
        this.uploadedMeters = getMeterDetailsPreview(this.data.consentRequest.meters);
      }
    }
  }

  ngOnInit(): void {
    // Log page view with dialog data
    this.logService.logPageView('add-meter-dialog', this.data);
    
    this.meterForm.get('selectedMeter')?.valueChanges.subscribe(selectedMeter => {
      if (selectedMeter) {
        this.logService.addUserActionLog('ADD_METER_ADDRESS_SELECTED', selectedMeter);
        // Reset the consent control when a new meter is selected
        this.meterForm.get('consentDetails.consent')?.reset();
        this.meterForm.get('consentDetails.consentTimestamp')?.reset();
      }
    });
    
    // Watch for consent changes to update timestamp
    this.meterForm.get('consentDetails.consent')?.valueChanges.subscribe((consentValue: Consent | null) => {
      if (consentValue) {
        this.logService.addConsentActionLog(consentValue.consentType);
        // Update timestamp when consent is provided
        this.updateConsentTimestamp();
      } else {
        // Clear timestamp when consent is removed
        this.meterForm.get('consentDetails.consentTimestamp')?.reset();
      }

      // Handle automatic submission for digital signature consents
      if (this.getConsentCategory() === ConsentCategory.LOA) {
        if (consentValue?.consentType === ConsentType.DIGITAL_SIGNATURE) {
          // Digital signature consent received
          console.log('Digital signature consent value:', consentValue);
          setTimeout(() => {
            this.submit();
          }, 500); // Ensure timestamp is set after the next change detection cycle
        }
      }
    });
  }
  
  isSmartMeterConsentType = isSmartMeterConsentType;

  getConsentCategory(): ConsentCategory {
    const selectedMeters: MeterDetails[] = this.meterForm.get('selectedMeter')?.value;

    if (selectedMeters.length === 1 && isSmartMeter(selectedMeters[0])) {
      return ConsentCategory.SMART_METER;
    }
    const smartMeters = selectedMeters.filter((meter: MeterDetails) => isSmartMeter(meter));

    // Check if all meters are smart and have the same address
    if (selectedMeters.length === smartMeters.length && smartMeters.length > 0) {
      const firstAddress = smartMeters[0].addressId;
      const sameAddress = smartMeters.every(meter => meter.addressId === firstAddress);
      if (sameAddress) {
        return ConsentCategory.SMART_METER; // All smart meters with same address
      }
    }

    if (this.scope === 'ANNUAL') {
      return ConsentCategory.CHECKBOX;
    } else if (this.scope === 'HH') {
      return ConsentCategory.LOA;
    }

    const nHHMeters = selectedMeters.filter((meter: MeterDetails) => isNonHHMeter(meter));

    if (selectedMeters.length === nHHMeters.length) {
      return ConsentCategory.CHECKBOX; // All selected meters are Non Half Hourly
    } else if (selectedMeters.length === smartMeters.length) {
      return ConsentCategory.CHECKBOX; // All selected meters are Smart to be considered as non-HH
    } else {
      return ConsentCategory.LOA; // At least one meter is Half Hourly
    }
  }

  getSelectedMeter(): MeterDetails | null {
    const selectedMeters = this.meterForm.get('selectedMeter')?.value;
    return Array.isArray(selectedMeters) && selectedMeters.length > 0 ? selectedMeters[0] : null;
  }

  // New method to get all selected meters
  getSelectedMeters(): MeterDetails[] {
    const selectedMeters = this.meterForm.get('selectedMeter')?.value;
    return Array.isArray(selectedMeters) ? selectedMeters : [];
  }

  // Method to handle meter selection from generic table (for multi-meter mode)
  onMeterSelectionChange(selectedRows: MeterPreview[]): void {
    console.log('Meter selection changed:', selectedRows);
    // Convert Meter[] to MeterDetails[] for consistency
    const selectedMeterDetails: MeterDetails[] = selectedRows.map(meter => ({
      meterNo: meter.meterNumber,
      fuelType: meter.fuelType,
      meterType: meter.meterType || MeterType.HALF_HOURLY,
      addressId: '', // Will be populated when we have address details
      addressAsLine: meter.address,
      currentSupplierName: 'Unknown Supplier',
      postCode: 'Unknown' // Use postCode from Meter model
    }));
    
    this.meterForm.get('selectedMeter')?.setValue(selectedMeterDetails);
  }

  // Method to get the count of selected meters for display
  getSelectedMeterCount(): number {
    return this.getSelectedMeters().length;
  }

  getMeterType(meterDetails: MeterDetails): MeterType {
   return meterDetails.meterType;
  }

  // Check if meter step is valid (meter selected, or multi-meter mode with uploaded meters)
  isMeterStepValid(): boolean {
    if (this.isMultiMeterMode) {
      // For multi-meter mode, check if meters are uploaded and some are selected
      return this.uploadedMeters.length > 0 && this.getSelectedMeters().length > 0;
    }
    
    const selectedMeters = this.meterForm.get('selectedMeter')?.value;
    return Array.isArray(selectedMeters) && selectedMeters.length > 0;
  }
  // Multi-meter Excel upload methods
  onExcelFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      //TODO:: Handle Excel file processing
    }
  }

  // Check if multi-meter step is valid
  isMultiMeterStepValid(): boolean {
    return this.isMultiMeterMode && this.uploadedMeters.length > 0 && this.getSelectedMeters().length > 0;
  }

  /**
   * Submit meter information and request consent if needed
   * Note: Both HH and Smart consent components now return standardized Consent objects,
   * making this submit method much simpler and more consistent.
   * Stripe payment processing is now handled in the service layer.
   */
  async submit(): Promise<void> {
    if (!this.meterForm.valid) return;

    const selectedMeters = this.getSelectedMeters();
    if (selectedMeters.length === 0) return;

    const { expiryDate, relationshipType, consent: consentData, email } = this.getFormData();
    
    // Since both consent components now return proper Consent objects,
    // we can use the consent directly without any transformation logic
    if (!consentData) {
      console.warn('No consent data available');
      this.closeDialogWithMeters();
      return;
    }

    const organisation = this.getOrganisationDetails();

    this.uploadingConsent = true;

    this.meterService.addNewMeter(
      consentData,
      selectedMeters,
      email,
      relationshipType,
      organisation,
      new Date(new Date().setDate(new Date().getDate() + 1)), // Start date (tomorrow)
      expiryDate
    )
    .pipe(
      finalize(() => this.uploadingConsent = false)
    )
    .subscribe({
      next: (response) => this.handleConsentSubmissionSuccess(response, selectedMeters, consentData),
      error: (error) => this.handleConsentSubmissionError(error)
    });
  }

  private getFormData() {
    return {
      expiryDate: this.meterForm.get('consentDetails.expiryDate')?.value,
      relationshipType: this.meterForm.get('relationship.type')?.value || RelationshipType.OCCUPIER,
      consent: this.meterForm.get('consentDetails.consent')?.value as Consent | null,
      email: this.loggedInUser?.email || 'user@example.com',
      consentTimestamp: this.meterForm.get('consentDetails.consentTimestamp')?.value
    };
  }

  private getOrganisationDetails() {
    return {
      name: this.loggedInUser?.companyName || 'Perse',
      role: this.loggedInUser?.role || 'Director',
      organisationNumber: this.loggedInUser?.companyId || '1234',
      organisationType: 'LTD'
    };
  }

  private handleConsentSubmissionSuccess(
    response: any,
    meters: MeterDetails[],
    consentData: Consent
  ): void {
    setTimeout(() => {
      // For smart meters, we might get a different response structure
      // Pass the response or requestId to the verification dialog
      const requestId = response?.data?.requestId || response?.requestId || 'smart-meter-request';
      this.openConsentVerificationDialog(requestId, meters, consentData);
    }, 1000); // Delay to ensure form updates are processed
  }

  private handleConsentSubmissionError(error: any): void {
    const errorMessage = this.isMultiMeterMode 
      ? 'Failed to submit meters for consent.' 
      : 'Failed to add address.';
    
    console.error('Error submitting consent:', error);
    this.utilService.showErrorMessageFromAPIResponse(errorMessage, error);
  }

  /**
   * Open the LoA verification dialog and handle the result
   */
  private openConsentVerificationDialog(requestId: string, selectedMeters: MeterDetails[], consentData: Consent): void {
    let config: VerificationDialogData;

    if (this.getConsentCategory() === ConsentCategory.SMART_METER) {
      config = VerificationDialogHelper.createSmartMeterVerificationConfig(
        requestId,
        selectedMeters.length,
        this.verificationAdapter
      );
    } else {
      config = VerificationDialogHelper.createConsentVerificationConfig(
        requestId,
        selectedMeters.length,
        this.verificationAdapter
      );
    }

    const dialogRef = this.dialog.open(VerificationDialogComponent, {
      width: '450px',
      disableClose: true, // Prevent closing by clicking outside
      data: config
    });
    
    dialogRef.afterClosed().subscribe(result => {
      if (result?.status === 'SUCCESS') {
        // Verification was successful, proceed with adding the meter
        this.closeDialogWithMeters(requestId);
      } else if (result?.status === 'FAILURE') {
        // User wants to retry the upload
        // We could reset the consent file field here or show a message
        console.log('User requested to retry consent upload');
        // this.utilService.openSnackBar('Something went wrong during consent verification. Please try again.', 'Close', 'error');
        // Optionally reset the consent file
        // this.meterForm.get('consentDetails.consent')?.reset();

        if (consentData.consentType === ConsentType.STRIPE) {
          // Reset Stripe state if consent type is STRIPE
          this.addSmartConsentComponent.resetStripe();
        }

        console.log(this.meterForm);
      } else {
        // Verification was cancelled or failed
        console.log('Consent verification cancelled or failed');
        this.utilService.openSnackBar('Consent verification was cancelled or failed.', 'Close', 'error');
      }
    });
  }

  private closeDialogWithMeters(requestId?: string): void {
    const selectedMeters = this.getSelectedMeters();

    // Convert MeterDetails to Meter objects for dialog close
    const updatedMeters: Meter[] = selectedMeters.map(meterDetail => ({
      meterNumber: meterDetail.meterNo,
      meterType: meterDetail.meterType,
      address: meterDetail.addressAsLine,
      consentStatus: ConsentStatus.ACTIVE,
      expiryDate: this.meterForm.get('consentDetails.expiryDate')?.value,
      fuelType: meterDetail.fuelType,
      startDate: new Date(new Date().setDate(new Date().getDate() + 1)), // Default start date to tomorrow
      relationship: this.meterForm.get('relationship.type')?.value
    }));

    let skippedMeters: Meter[] = [];
    if (this.data.consentRequest?.meters) {
      skippedMeters = this.data.consentRequest.meters.filter(
        m => !updatedMeters.some(added => added.meterNumber === m.mpxn
      )).map(m => ({
        meterNumber: m.mpxn,
        meterType: m.type as MeterType,
        address: m.address || '',
        consentStatus: ConsentStatus.PENDING, // Default to PENDING for skipped meters
        fuelType: m.fuelType as FuelType,
        startDate: new Date(new Date().setDate(new Date().getDate() + 1)), // Default start date to tomorrow
        expiryDate: this.meterForm.get('consentDetails.expiryDate')?.value,
        relationship: this.meterForm.get('relationship.type')?.value
      }));
    }

    this.dialogRef.close({requestId, meters: updatedMeters, skippedMeters});
  }

  // New methods for relationship step
  onRelationshipTypeChange(relationshipType: string): void {
    // Set validators for documentation in Step 3
    // this.updateDocumentationValidators(relationshipType);
  }

  isRelationshipStepCompleted(): boolean {
    const relationshipGroup = this.meterForm.get('relationship');
    if (!relationshipGroup) return false;

    const relationshipType = relationshipGroup.get('type')?.value;
    return !!relationshipType; // Simply check if a relationship type is selected
  }

  cancel(): void {
    const isSkip = this.isConsentRequestMode;
    const title = isSkip ? 'Are you sure you want to skip?' : 'Are you sure you want to cancel?';
    const message = isSkip
      ? 'This will mean that your consent won\'t be added to the wallet now. You can revisit this request later from the pending requests in your dashboard.'
      : 'This will mean that your consent won\'t be added to the wallet.';
      
    this.utilService.openPopup(title, message, 'YesOrNo').subscribe(result => {
      if (result) {
        this.sdkManager.messageAndTerminateSession({ status: 'FAILURE', message: 'REQUEST_CANCELLED' });
        this.dialogRef.close();
        this.logService.addUserActionLog(isSkip ? 'SKIP_ADD_METER' : 'CANCEL_ADD_METER');
      }
    });
  }

  /**
   * Method to update consent timestamp when consent is provided
   */
  private updateConsentTimestamp(): void {
    const now = new Date().toISOString();
    this.meterForm.get('consentDetails.consentTimestamp')?.setValue(now);
  }

  /**
   * Create Stripe payment method for credit card consent
   * @param consent The consent object containing Stripe payment element and credentials
   * @returns Promise with payment method creation result
   */
  /**
   * Decline the consent request - updates notification status to DISMISSED
   */
  decline(): void {
    if (!this.isConsentRequestMode || !this.data.consentRequest?.consentRequestId) {
      return;
    }

    this.utilService.openPopup(
      'Decline Request?',
      'Are you sure you want to decline this consent request? This action cannot be undone.',
      'YesOrNo'
    ).subscribe(result => {
      if (result) {
        this.logService.addUserActionLog('DECLINE_ADD_METER');
        this.notificationService.updateNotificationStatus(
          this.data.consentRequest!.consentRequestId,
          NotificationStatus.DISMISSED
        ).subscribe({
          next: (success) => {
            if (success) {
              this.sdkManager.messageAndTerminateSession({ status: 'FAILURE', message: 'REQUEST_DECLINED'  });
              // this.utilService.openSnackBar('Request declined successfully', 'Close', 'success');
              this.dialogRef.close({ declined: true });
            } else {
              this.utilService.openSnackBar('Failed to decline request', 'Close', 'error');
            }
          },
          error: (error) => {
            console.error('Error declining consent request:', error);
            this.utilService.openSnackBar('Failed to decline request', 'Close', 'error');
          }
        });
      }
    });
  }

  /**
   * Automatically scrolls to the end of the dialog content
   * This function can be called whenever you need to scroll the dialog to the bottom
   */
  scrollToEndOfDialog(): void {
    if (this.dialogContent?.nativeElement) {
      const element = this.dialogContent.nativeElement;
      // Use setTimeout to ensure DOM has been updated before scrolling
      setTimeout(() => {
        element.scrollTo({
          top: element.scrollHeight,
          behavior: 'smooth'
        });
      }, 500);
    }
  }
}
